import { FC, Suspense } from 'react';
import { Route, Routes } from 'react-router';

import { LayoutRoutes } from '@/layout/layout-routes.tsx';
import { ActionsRoutes } from '@/modules/Actions/actions-routes.tsx';
import { AuthRoutes } from '@/modules/Auth/auth-routes.tsx';
import { EquipmentRoutes } from '@/modules/Equipment/equipment-routes.tsx';
import { ErrorRoutes } from '@/modules/Error/error-routes.tsx';
import { HomeRoutes } from '@/modules/Home/home-routes.tsx';
import { LicencesRoutes } from '@/modules/Licences/licences-routes.tsx';
import { ReleasesRoutes } from '@/modules/Releases/releases-routes.tsx';
import { ReportsRoutes } from '@/modules/Reports/reports-routes.tsx';
import { ReservationsRoutes } from '@/modules/Reservations/reservations-routes.tsx';
import { SettingsRoutes } from '@/modules/Settings/settings-routes.tsx';
import { PermissionName } from '@/modules/Settings/types/access-control.const.ts';
import { SubcontractorsRoutes } from '@/modules/Subcontractors/subcontractors-routes.tsx';
import { RequiresPermission } from '@/shared/components/access-control/RequiresPermission.tsx';
import { Loader } from '@/shared/components/interactions/Loader';
import { useMixpanelConfigUpdate } from '@/shared/hooks/global/useMixpanelConfigUpdate.ts';
import { newRelicService } from '@/shared/services/newrelic.service';

import { RoutesEnum } from '@/shared/types/global/enums.ts';

newRelicService.init();

export const AppRoutes: FC = () => {
  useMixpanelConfigUpdate();

  return (
    <Suspense fallback={<Loader appearance="suspense" />}>
      <Routes>
        <Route element={<LayoutRoutes.Error />}>
          <Route element={<LayoutRoutes.Updater />}>
            {/*Public Routes*/}
            <Route element={<LayoutRoutes.Auth />}>
              <Route element={<AuthRoutes.Login />} path={RoutesEnum.LOGIN} />
              <Route element={<AuthRoutes.MFASetup />} path={RoutesEnum.MFA} />
              <Route element={<AuthRoutes.Terms />} path={RoutesEnum.TERMS} />
              <Route element={<AuthRoutes.SiteSelect />} path={RoutesEnum.SITE_SELECT} />
              <Route element={<AuthRoutes.RestorePassword />} path={RoutesEnum.RESTORE_PASSWORD} />
              <Route element={<AuthRoutes.MobileAuth />} path={RoutesEnum.MOBILE_AUTH} />
              <Route element={<AuthRoutes.TermsOfUse />} path={RoutesEnum.TERMS_OF_USE} />
            </Route>

            {/*Private Routes*/}
            <Route element={<LayoutRoutes.Secure />}>
              <Route element={<LayoutRoutes.Connection />}>
                <Route element={<LayoutRoutes.Layout />} path={RoutesEnum.HOME}>
                  <Route element={<AuthRoutes.Logout />} path={RoutesEnum.LOGOUT} />

                  {/*Home*/}
                  <Route
                    element={
                      <RequiresPermission
                        className="h-[calc(100vh-7rem)]"
                        name={PermissionName.TABS_MAP_TAB}
                        showNoneNotice
                      >
                        <HomeRoutes.Home />
                      </RequiresPermission>
                    }
                    index
                  />

                  {/*Reservations*/}
                  <Route
                    element={
                      <RequiresPermission
                        className="h-[calc(100vh-7rem)]"
                        name={PermissionName.TABS_RESERVATIONS_TAB}
                        showNoneNotice
                      >
                        <ReservationsRoutes.Reservations />
                      </RequiresPermission>
                    }
                    path={RoutesEnum.RESERVATIONS}
                  >
                    <Route element={<ReservationsRoutes.Schedule />} index />
                    <Route
                      element={<ReservationsRoutes.History />}
                      path={RoutesEnum.RESERVATIONS_HISTORY}
                    />
                  </Route>

                  {/*Equipment*/}
                  <Route
                    element={
                      <RequiresPermission
                        className="h-[calc(100vh-7rem)]"
                        name={PermissionName.TABS_EQUIPMENT_TAB}
                        showNoneNotice
                      >
                        <EquipmentRoutes.Equipment />
                      </RequiresPermission>
                    }
                    path={RoutesEnum.EQUIPMENT}
                  >
                    <Route element={<EquipmentRoutes.Equipments />} index />
                    <Route element={<EquipmentRoutes.EquipmentItem />} path=":equipment-id" />
                    <Route element={<EquipmentRoutes.Groups />} path="groups" />
                    <Route path="groups/:group-id" />
                  </Route>

                  {/*People*/}
                  <Route
                    element={
                      <RequiresPermission
                        className="h-[calc(100vh-7rem)]"
                        name={PermissionName.TABS_PEOPLE_TAB}
                        showNoneNotice
                      >
                        <SubcontractorsRoutes.Subcontractors />
                      </RequiresPermission>
                    }
                    path={RoutesEnum.PEOPLE}
                  >
                    <Route
                      element={
                        <RequiresPermission
                          className="h-[calc(100vh-7rem)]"
                          name={PermissionName.PEOPLE_SHOW_ALL_FIRMS}
                          showNoneNotice
                        >
                          <SubcontractorsRoutes.Firms />
                        </RequiresPermission>
                      }
                      index
                    />

                    <Route
                      element={<SubcontractorsRoutes.SubcontractorItem />}
                      path=":subcontractor-id"
                    >
                      <Route element={<SubcontractorsRoutes.Details />} index />
                      <Route element={<SubcontractorsRoutes.WorkerProfile />} path=":worker-id" />
                      <Route element={<SubcontractorsRoutes.Reservations />} path="reservations" />
                    </Route>

                    <Route element={<SubcontractorsRoutes.Groups />} path="groups" />
                    <Route path="groups/:group-id" />
                  </Route>

                  {/*Reports*/}
                  <Route
                    element={
                      <RequiresPermission
                        className="h-[calc(100vh-7rem)]"
                        name={PermissionName.TABS_REPORTS_TAB}
                        showNoneNotice
                      >
                        <ReportsRoutes.Reports />
                      </RequiresPermission>
                    }
                    path={RoutesEnum.REPORTS}
                  >
                    <Route
                      element={
                        <RequiresPermission
                          className="h-[calc(100vh-7rem)]"
                          name={PermissionName.REPORTS_TIME_IN_ZONE_REPORT_FOR_EQUIPMENT}
                          showNoneNotice
                        >
                          <ReportsRoutes.TimeZone />
                        </RequiresPermission>
                      }
                      index
                    />
                    <Route
                      element={
                        <RequiresPermission
                          className="h-[calc(100vh-7rem)]"
                          name={PermissionName.REPORTS_UTILIZATION_REPORT}
                          showNoneNotice
                        >
                          <ReportsRoutes.Utilization />
                        </RequiresPermission>
                      }
                      path="utilization"
                    />
                    <Route
                      element={
                        <RequiresPermission
                          className="h-[calc(100vh-7rem)]"
                          name={PermissionName.REPORTS_TRIPS_REPORT}
                          showNoneNotice
                        >
                          <ReportsRoutes.Trips />
                        </RequiresPermission>
                      }
                      path="trips"
                    />
                    <Route
                      element={
                        <RequiresPermission
                          className="h-[calc(100vh-7rem)]"
                          name={PermissionName.REPORTS_SITE_PRESENCE_REPORT_FOR_PEOPLE}
                          showNoneNotice
                        >
                          <ReportsRoutes.SitePresence />
                        </RequiresPermission>
                      }
                      path="site-presence"
                    />
                    <Route
                      element={
                        <RequiresPermission
                          className="h-[calc(100vh-7rem)]"
                          name={PermissionName.REPORTS_TAG_DIAGNOSTICS_REPORT_FOR_EQUIPMENT}
                          showNoneNotice
                        >
                          <ReportsRoutes.TagDiagnostics />
                        </RequiresPermission>
                      }
                      path="tag-diagnostics"
                    />
                    <Route
                      element={
                        <RequiresPermission
                          className="h-[calc(100vh-7rem)]"
                          name={PermissionName.REPORTS_AUDIT_LOG_REPORT}
                          showNoneNotice
                        >
                          <ReportsRoutes.AuditLog />
                        </RequiresPermission>
                      }
                      path="audit-log"
                    />
                    <Route
                      element={
                        <RequiresPermission
                          className="h-[calc(100vh-7rem)]"
                          name={PermissionName.REPORTS_ZONE_ENTRY_EXIT_TIMES_REPORT}
                          showNoneNotice
                        >
                          <ReportsRoutes.ZoneEntryExit />
                        </RequiresPermission>
                      }
                      path="zone-entry-exit"
                    />
                  </Route>

                  {/*Actions*/}
                  <Route
                    element={
                      <RequiresPermission
                        className="h-[calc(100vh-7rem)]"
                        name={PermissionName.TABS_ACTIONS_TAB}
                        showNoneNotice
                      >
                        <ActionsRoutes.Actions />
                      </RequiresPermission>
                    }
                    path={RoutesEnum.ACTIONS}
                  />

                  {/*Settings*/}
                  <Route element={<SettingsRoutes.Settings />} path={RoutesEnum.SETTINGS}>
                    <Route
                      element={
                        <RequiresPermission
                          className="h-[calc(100vh-7rem)]"
                          name={PermissionName.SETTINGS_PROFILE_TAB}
                          showNoneNotice
                        >
                          <SettingsRoutes.Profile />
                        </RequiresPermission>
                      }
                      index
                    />
                    <Route
                      element={
                        <RequiresPermission
                          className="h-[calc(100vh-7rem)]"
                          name={PermissionName.SETTINGS_SITE_HOURS_TAB}
                          showNoneNotice
                        >
                          <SettingsRoutes.SiteHours />
                        </RequiresPermission>
                      }
                      path="site-hours"
                    />
                    <Route
                      element={
                        <RequiresPermission
                          className="h-[calc(100vh-7rem)]"
                          name={PermissionName.SETTINGS_SITE_MAP_TAB}
                          showNoneNotice
                        >
                          <SettingsRoutes.SiteMap />
                        </RequiresPermission>
                      }
                      path="site-map"
                    />
                    <Route
                      element={
                        <RequiresPermission
                          className="h-[calc(100vh-7rem)]"
                          name={PermissionName.SETTINGS_MANAGE_ACCESS_CONTROL}
                          showNoneNotice
                        >
                          <SettingsRoutes.AccessControl />
                        </RequiresPermission>
                      }
                      path="access-control"
                    />
                    <Route
                      element={
                        <RequiresPermission
                          className="h-[calc(100vh-7rem)]"
                          name={PermissionName.SETTINGS_ACTIONS_TAB}
                          showNoneNotice
                        >
                          <SettingsRoutes.Actions />
                        </RequiresPermission>
                      }
                      path="actions"
                    />
                    <Route
                      element={
                        <RequiresPermission
                          className="h-[calc(100vh-7rem)]"
                          name={PermissionName.SETTINGS_AUTO_APPROVAL_TAB}
                          showNoneNotice
                        >
                          <SettingsRoutes.AutoApproval />
                        </RequiresPermission>
                      }
                      path="auto-approval"
                    />
                    <Route
                      element={
                        <RequiresPermission
                          className="h-[calc(100vh-7rem)]"
                          name={PermissionName.SETTINGS_ZPS_ACCESS_FOR_OWN_FIRM}
                          showNoneNotice
                        >
                          <SettingsRoutes.ZPSAccess />
                        </RequiresPermission>
                      }
                      path="zps-access"
                    >
                      <Route element={<SettingsRoutes.ZPSAccess />} path=":worker-id" />
                    </Route>
                    <Route
                      element={
                        <RequiresPermission
                          className="h-[calc(100vh-7rem)]"
                          name={PermissionName.SETTINGS_SITE_MANAGEMENT}
                          showNoneNotice
                        >
                          <SettingsRoutes.SiteManagement  />
                        </RequiresPermission>
                      }
                      path="site-management"
                    >
                      <Route element={<SettingsRoutes.SiteManagement />} path=":site-id" />
                    </Route>
                    <Route
                      element={
                        <RequiresPermission name={PermissionName.SETTINGS_DEVICE_MANAGEMENT_TAB}>
                          <SettingsRoutes.DeviceManagement />
                        </RequiresPermission>
                      }
                      path="device-management"
                    >
                      <Route element={<SettingsRoutes.Tags />} index />
                      <Route element={<SettingsRoutes.Hubs />} path="hubs" />
                      <Route element={<SettingsRoutes.Readers />} path="readers" />
                      <Route element={<SettingsRoutes.Devices5G />} path="5g-devices" />
                    </Route>
                  </Route>

                  {/*Releases*/}
                  <Route element={<ReleasesRoutes.Releases />} path={RoutesEnum.RELEASES} />

                  {/*Licences*/}
                  <Route element={<LicencesRoutes.Licences />} path={RoutesEnum.LICENSES} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>

        <Route element={<ErrorRoutes.ErrorPage />} path="*" />
      </Routes>
    </Suspense>
  );
};
